import Dialog from "@mui/material/Dialog"
import {
  DrawerContent,
  PrimaryButton,
  StyledButton,
  StyledCancelReconcileButton,
  StyledScheduleBox,
} from "./styles"
import { Box, DialogContentText, DialogTitle, IconButton } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import {
  cancelProcessSchedule,
  initiateProcessSchedule,
  reinitiateProcessSchedule,
  selectTransactions,
} from "../../slices/transactionsSlice"
import dayjs from "dayjs"
import duration from "dayjs/plugin/duration"
import { processScheduleStatus } from "../../constants/data"

type ScheduleReconciliationPopupProps = {
  open: boolean
  onClose: () => void
}

const useCountdown = (endTime: Date) => {
  dayjs.extend(duration)
  const [countdown, setCountdown] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  })

  useEffect(() => {
    const intervalId = setInterval(() => {
      const remainingTime = endTime.getTime() - Date.now()
      const duration = dayjs.duration(remainingTime)

      setCountdown({
        hours: Math.floor(duration.asHours()) % 60,
        minutes: Math.floor(duration.asMinutes()) % 60,
        seconds: Math.floor(duration.asSeconds()) % 60,
      })
    }, 1000)

    return () => clearInterval(intervalId)
  }, [endTime])

  const formattedCountdown =
    countdown.hours > 0
      ? ` (${pad(countdown.hours)}:${pad(countdown.minutes)}:${pad(
          countdown.seconds,
        )} h)`
      : ` (${pad(countdown.minutes)}:${pad(countdown.seconds)} m)`

  return formattedCountdown
}

const pad = (num: number) => {
  return num.toString().padStart(2, "0")
}

const ScheduleReconciliationPopup = ({
  open,
  onClose,
}: ScheduleReconciliationPopupProps) => {
  const { processScheduleData } = useAppSelector(selectTransactions)
  const dispatch = useAppDispatch()

  const endTime = dayjs(
    processScheduleData.nextRun,
    "DD/MM/YYYY hh:mm A",
  ).toDate()
  const countdown = useCountdown(endTime)

  const handleProcessSchedule = () => {
    processScheduleData.id
      ? dispatch(reinitiateProcessSchedule())
      : dispatch(initiateProcessSchedule())
    onClose()
  }
  const cancelSchedule = () => {
    dispatch(cancelProcessSchedule())
    onClose()
  }

  let title = "Schedule Reconciliation"
  let confirmText = "Yes, Schedule"
  let message =
    "Are you sure you want to schedule reconciliation? This will start the process in 15 minutes."
  let cancelText = "Cancel"
  if (processScheduleData.id) {
    title = "Reschedule Reconciliation"
    confirmText = "Reschedule"
    message =
      "You can reschedule the current reconciliation or cancel it entirely"
    cancelText = "Close"
  }
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle sx={{ m: 0, pb: 0, fontWeight: 700 }}>{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: "absolute",
          right: 4,
          top: 4,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <DrawerContent>
        <DialogContentText px={"2px"} fontSize={"14px"} fontWeight={600}>
          {message}
        </DialogContentText>
        {processScheduleData.scheduleStatus ==
          processScheduleStatus.PENDING && (
          <StyledScheduleBox>
            Current schedule:{" "}
            <span>
              {processScheduleData.nextRun}
              {countdown}
            </span>
          </StyledScheduleBox>
        )}
        <Box
          sx={{
            display: "flex",
            gap: "10px",
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <StyledButton variant="outlined" color="inherit" onClick={onClose}>
            {cancelText}
          </StyledButton>
          {processScheduleData.id && (
            <StyledCancelReconcileButton
              variant="outlined"
              color="inherit"
              onClick={cancelSchedule}
            >
              Cancel Schedule
            </StyledCancelReconcileButton>
          )}
          <PrimaryButton variant="contained" onClick={handleProcessSchedule}>
            {confirmText}
          </PrimaryButton>
        </Box>
      </DrawerContent>
    </Dialog>
  )
}

export default ScheduleReconciliationPopup
